'use client'

import React, { useCallback, useEffect, useState } from 'react'

import BookingOptions from '@/components/booking/bookingOptions'
import BookingWizardModal from '@/components/booking/booking-wizard-modal'
import VisitUs from '@/components/contact/VisitUs'
import CrisisGuidance from '@/components/CrisisGuidance'
import {
  clearBookingDraftMarker,
  clearUnsafeBookingDraftStorage,
  hasValidBookingDraftMarker,
} from '@/lib/booking-draft-marker'
import type { BookingData } from '@/types/booking'

const createInitialBookingData = (): BookingData => ({
  name: '',
  dob: undefined,
  gender: '',
  phone: '',
  email: '',

  doctorID: '',
  doctorName: '',
  doctorSpecialty: '',

  bookingDate: undefined,
  slot: '',
  status: 'Booked',

  flowType: null,
  assessmentType: null,

  existingPatientId: null,
  existingPatientHumanId: null,
  phoneVerified: false,
  preferredPsychologist: null,
  patientMode: null,
  durationMinutes: undefined,
})

const BookAppointmentPage: React.FC = () => {
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const [hasBookingDraft, setHasBookingDraft] = useState(false)
  const [bookingData, setBookingData] = useState<BookingData>(
    createInitialBookingData,
  )

  const refreshDraftState = useCallback(() => {
    setHasBookingDraft(hasValidBookingDraftMarker())
  }, [])

  useEffect(() => {
    clearUnsafeBookingDraftStorage()
    refreshDraftState()

    window.addEventListener('storage', refreshDraftState)
    window.addEventListener('focus', refreshDraftState)

    return () => {
      window.removeEventListener('storage', refreshDraftState)
      window.removeEventListener('focus', refreshDraftState)
    }
  }, [refreshDraftState])

  const handleOpenBooking = () => {
    setIsWizardOpen(true)
  }

  const handleContinueBooking = () => {
    setIsWizardOpen(true)
  }

  const handleStartNewBooking = () => {
    clearBookingDraftMarker()
    clearUnsafeBookingDraftStorage()
    setHasBookingDraft(false)
    setBookingData(createInitialBookingData())
    setIsWizardOpen(true)
  }

  const handleWizardClose = () => {
    setIsWizardOpen(false)
    refreshDraftState()
  }

  return (
    <main className="min-h-screen bg-gray-50 pt-20">
      <section className="border-b bg-white">
        <div className="mx-auto max-w-7xl px-4 py-10 text-center sm:px-6 sm:py-12 md:py-16 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl md:text-5xl">
            Book an Appointment
          </h1>

          <p className="mx-auto mt-3 max-w-3xl px-2 text-base text-gray-600 sm:mt-4 sm:px-0 sm:text-lg md:text-xl">
            Verify your phone number first, then choose the right care pathway.
          </p>
        </div>
      </section>

      {/* Urgent-help guidance (P2.13, approved wording): routine-vs-emergency
          separation BEFORE the visitor reaches booking controls. */}
      <CrisisGuidance variant="card" />

      <BookingOptions
        hasBookingDraft={hasBookingDraft}
        onOpenModal={handleOpenBooking}
        onContinueBooking={handleContinueBooking}
        onStartNewBooking={handleStartNewBooking}
      />

      {/* Clinic location, hours and contact fallback (identity-driven).
          Booking controls stay above; this is the "where/when" surface. */}
      <VisitUs showBookCta={false} />

      <BookingWizardModal
        isOpen={isWizardOpen}
        onClose={handleWizardClose}
        bookingData={bookingData}
        setBookingData={setBookingData}
        createInitialBookingData={createInitialBookingData}
      />
    </main>
  )
}

export default BookAppointmentPage
